/*
 * See the file LICENSE for the full license governing this code.
 */

import * as React from 'react';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import PaperOnCenter from '../components/PaperOnCenter';
import { submitVerification, useAuth } from '../components/authentication';
import { useMutation } from 'react-query';
import { networkWithErrorConvert } from '../api';
import ErrorMessageWrapper from '../components/ErrorMessageWrapper';
import { Navigate, useNavigate } from 'react-router-dom';

export function VerifyEmailCommon({
  Component,
  mutation,
  disabled,
  OptionalIputs,
  OptionalSecondaryAction,
  SuccessMessage = <Typography sx={{ pb: 2 }}>Email is confirmed</Typography>,
}) {
  const resendPassword = useMutation(() =>
    networkWithErrorConvert({ url: '/v1/user/resend-otp', method: 'post' })
  );
  const m = mutation();
  return (
    <Component
      submitButtonProps={{ disabled }}
      Title="Verify Email"
      Inputs={
        <>
          {OptionalIputs}
          <TextField
            disabled={disabled}
            data-cy="otp-input"
            margin="normal"
            required
            fullWidth
            id="otp"
            label="Verification code"
            name="otp"
            type="text"
            autoFocus
          />
        </>
      }
      SuccessMessage={SuccessMessage}
      SecondaryAction={
        <Stack spacing={1}>
          <LoadingButton
            data-cy="resend-otp"
            variant="outlined"
            loading={resendPassword.isLoading}
            disabled={disabled || m.isLoading}
            onClick={() => {
              resendPassword.reset();
              resendPassword.mutate();
            }}
          >
            Resend email verification code
          </LoadingButton>
          {OptionalSecondaryAction}
          {resendPassword.isSuccess ? (
            <Typography>
              Verification code is sent to your email. Please check your inbox
            </Typography>
          ) : null}
          {resendPassword.isError ? (
            <ErrorMessageWrapper>
              <Typography>Failed to resend the verification code</Typography>
            </ErrorMessageWrapper>
          ) : null}
        </Stack>
      }
      SubmitButtonTitle="Confirm email"
      mutation={() => m}
    />
  );
}

export default function VerifyEmail({ navigateTo }) {
  const { username, signout } = useAuth();
  const navigate = useNavigate();
  if (!username) {
    return <Navigate to="/" replace />;
  }
  return (
    <VerifyEmailCommon
      mutation={() => {
        const disableOnSuccess = navigateTo != null;
        return submitVerification({ disableOnSuccess });
      }}
      SuccessMessage={navigateTo && <Navigate to={navigateTo} replace />}
      Component={PaperOnCenter}
      OptionalIputs={
        <Typography align="left" sx={{ pt: 1, wordBreak: 'break-all' }}>
          Verification code was sent to {username}
        </Typography>
      }
      OptionalSecondaryAction={
        <Button
          variant="outlined"
          onClick={() => {
            signout();
            navigate('/signup');
          }}
        >
          Sign up with different email
        </Button>
      }
    />
  );
}
